import { FunctionComponent, useContext, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import classnames from 'classnames';

import { Option } from 'types/options';

import { Types } from 'models/CartItem';
import { ProductTypes } from 'models/Product';

import { getOptionValue } from 'utils/option';
import Tracking from 'services/Tracking';

import LocaleContext from 'components/context/Locale';
import NewImage from 'components/layout/NewImage';
import LayoutP24Instalments from 'components/layout/P24Instalments';
import ProductSelect from 'components/layout/ProductSelect';
import AddToCartButton from 'components/modules/public/products/AddToCartButton';
import BuyNowButton from 'components/modules/public/products/BuyNowButton';

import { Props } from './index';
import StyledComponent from './styles';

const Portal = dynamic(() => import('components/modules/public/layout/modals/Portal'), { ssr: false });
const ProductBoxBuyNowModal = dynamic(() => import('components/modules/public/products/ProductBoxBuyNowModal'), { ssr: false });

const PublicLayoutOfferProductCard: FunctionComponent<Props> = ({ product, labels, content, state, actions }) => {
    const {
        id,
        locale,
        duration,
        recommendedQuantityFrom,
        recommendedQuantityTo,
        defaultPricingMatrix,
        defaultOngoingPromoPricingMatrix,
        finalPricingMatrixOptions,
        ongoingPromo,
        promoPercent,
        pricing,
        productType,
        name,
        category,
    } = product;
    const [currentPricingMatrixOption, setCurrentPricingMatrixOption] = useState<Option<number>>(null);
    const [buyNowModalVisible, setBuyNowModalVisible] = useState<boolean>(false);
    const localeContext = useContext(LocaleContext);

    const visibleLabels = labels.filter(label => label.visible !== false);
    const packagePriceDisplay = productType === ProductTypes.Package && pricing?.priceDisplay;
    const packageOnGoingPromoPriceDisplay = packagePriceDisplay && ongoingPromo && ongoingPromo.pricing.priceDisplay;
    const translations = localeContext?.translations?.blockSectionProducts;

    // Price to display
    const isSpecialOfferInCart = !!state?.cart?.items?.find((cartItem) => cartItem?.item?.id === id);
    const isSpecialOffer = product?.isSpecialOffer;

    // Price for instalments component
    const priceToInstalments = ongoingPromo && promoPercent > 0
        ? currentPricingMatrixOption?.['price'] || (productType === ProductTypes.Package && ongoingPromo?.pricing?.price) || defaultOngoingPromoPricingMatrix?.price
        : currentPricingMatrixOption?.['price'] || (productType === ProductTypes.Package && pricing?.price) || defaultPricingMatrix?.price;

    useEffect(() => {
        Tracking.eventProductDetail(id, name, category?.locales?.[0]?.name);
    }, [id]);

    const onCreateCartOffer = async () => {
        return await actions.createCartItem({
            cartId: state?.cart?.id,
            itemId: id,
            itemType: Types.Product,
            quantity: getOptionValue(currentPricingMatrixOption) || 1,
        });
    };

    return (
        <StyledComponent
            className="public-layout-offer-product-card"
        >
            <div className="product">
                <div className="image-container">
                    <NewImage
                        className="product-image"
                        fileUpload={product?.locale?.image}
                        resizerConfig={{ width: 600 }}
                        alt={product?.locale?.name}
                    />
                </div>
                <div className="content-container">
                    {visibleLabels.length > 0 && (
                        <div className="special-offer-labels">
                            {visibleLabels.map(label => (
                                <div
                                    className="label"
                                    key={label.label}
                                >
                                    {label.label}
                                </div>
                            ))}
                        </div>
                    )}
                    {(ongoingPromo && promoPercent > 0 && !currentPricingMatrixOption?.data?.promoProcent) && (
                        <div className={classnames('discount-desktop', {
                            'higher': Boolean(product?.showLowestPrice),
                        })}
                        >
                            -{promoPercent}%
                        </div>
                    )}
                    {currentPricingMatrixOption?.data?.promoProcent > 0 && (
                        <div className={classnames('discount-desktop', {
                            'higher': Boolean(product?.showLowestPrice),
                        })}
                        >
                            -{currentPricingMatrixOption?.data?.promoProcent}%
                        </div>
                    )}
                    <div className="header">
                        {product?.locale?.name && (
                            <div className="headline">
                                <h1 dangerouslySetInnerHTML={{ __html: product?.locale?.name }} />
                            </div>
                        )}
                        <div className="mobile-image-wrapper">
                            <NewImage
                                className="product-image-mobile"
                                fileUpload={product?.locale?.image}
                                resizerConfig={{ width: 600 }}
                                alt={product?.locale?.name}
                            />
                            {(ongoingPromo && promoPercent > 0 && !currentPricingMatrixOption?.data?.promoProcent) && (
                                <div className={classnames('discount-mobile', {
                                    'higher': Boolean(product?.showLowestPrice),
                                })}
                                >
                                    -{promoPercent}%
                                </div>
                            )}
                            {currentPricingMatrixOption?.data?.promoProcent > 0 && (
                                <div className={classnames('discount-mobile', {
                                    'higher': Boolean(product?.showLowestPrice),
                                })}
                                >
                                    -{currentPricingMatrixOption?.data?.promoProcent}%
                                </div>
                            )}
                        </div>
                        <div className="old-price-wrapper">
                            {product?.showProductDuration && (
                                <div className="offer-duration-container">
                                    {translations?.product?.productDurationTo}
                                    <span>
                                        {product?.productDurationToDisplay?.day}
                                        {' '}
                                        {translations?.product?.to}
                                        {' '}
                                        {product?.productDurationToDisplay?.hour}
                                    </span>
                                </div>
                            )}
                            {product?.showLowestPrice && (
                                <div className="last-month-price-container">
                                    <div className="info-mark">
                                        i
                                        <div className="info-mark__information">
                                            {translations?.product?.tooltipInfo}
                                        </div>
                                    </div>
                                    {translations?.product?.lowestPrice}
                                    <span>
                                        {product?.displayLowestPriceValue}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="price-data-container">
                            <div className="prices-container">
                                {(!ongoingPromo || (ongoingPromo && promoPercent <= 0)) && (
                                    <>
                                        <div
                                            className="pricing-price"
                                            dangerouslySetInnerHTML={{ __html: currentPricingMatrixOption?.['priceDisplay'] || packagePriceDisplay || defaultPricingMatrix?.priceDisplay }}
                                        />
                                        {(currentPricingMatrixOption?.data?.regularPriceDisplay && Boolean(currentPricingMatrixOption?.value > 1)) && (
                                            <div
                                                className="pricing-old-price"
                                                dangerouslySetInnerHTML={{ __html: currentPricingMatrixOption?.data?.regularPriceDisplay }}
                                            />
                                        )}
                                    </>
                                )}
                                {(ongoingPromo && promoPercent > 0) && (
                                    <>
                                        <div
                                            className="pricing-price"
                                            dangerouslySetInnerHTML={{ __html: currentPricingMatrixOption?.['priceDisplay'] || packageOnGoingPromoPriceDisplay || defaultOngoingPromoPricingMatrix?.priceDisplay }}
                                        />
                                        <div
                                            className="pricing-old-price"
                                            dangerouslySetInnerHTML={{ __html: currentPricingMatrixOption?.data?.regularPriceDisplay || packagePriceDisplay || defaultPricingMatrix?.priceDisplay }}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                        {product?.locale?.description && (
                            <div
                                className="description"
                                dangerouslySetInnerHTML={{ __html: product?.locale?.description }}
                            />
                        )}
                    </div>

                    {content && (
                        <div className="content-list">
                            <div className="content">
                                <div className="title">
                                    <img
                                        className="icon"
                                        src="/images/product/duration.svg"
                                        loading="lazy"
                                        alt=""
                                    />
                                    <span className="title-gold">{content?.titles?.duration}</span>
                                    <span>{duration?.toString()} min</span>
                                </div>
                                {(recommendedQuantityFrom && recommendedQuantityTo) && (
                                    <div className="title">
                                        <img
                                            className="icon"
                                            src="/images/product/recommended-quantity.svg"
                                            loading="lazy"
                                            alt=""
                                        />
                                        <span className="title-gold">{content?.titles?.recommendation}</span>
                                        <span>{`${recommendedQuantityFrom} - ${recommendedQuantityTo}`}</span>
                                    </div>
                                )}
                                {product?.packageQuantity && Number(product?.packageQuantity) > 0 ? (
                                    <div className="title">
                                        <img
                                            className="icon"
                                            src="/images/product-info/heart.svg"
                                            loading="lazy"
                                            alt=""
                                        />
                                        <span className="title-gold">{content?.titles?.quantity || translations?.product?.quantity}</span>
                                        <span>{product?.packageQuantity}</span>
                                    </div>
                                ): null}
                                {locale?.target && (
                                    <div className="title">
                                        <img
                                            className="icon"
                                            src="/images/product/target.svg"
                                            loading="lazy"
                                            alt=""
                                        />
                                        <span className="title-gold">{content?.titles?.target}</span>
                                        <span>{locale?.target}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="footer">
                        <div className="footer-controls-state">
                            {(finalPricingMatrixOptions.length > 0 && !isSpecialOffer) && (
                                <ProductSelect
                                    options={finalPricingMatrixOptions}
                                    onChange={(selectedOption: Option<number>) => setCurrentPricingMatrixOption(selectedOption)}
                                    value={currentPricingMatrixOption}
                                />
                            )}
                        </div>
                        <div className="footer-controls">
                            <BuyNowButton
                                label={translations?.product?.buttons?.buyNow}
                                onClick={() => setBuyNowModalVisible(true)}
                            />
                            <AddToCartButton
                                label={translations?.product?.buttons?.addToCart}
                                onClick={onCreateCartOffer}
                                disabled={isSpecialOfferInCart && isSpecialOffer}
                            />
                        </div>
                    </div>

                    <div className="instalments">
                        <LayoutP24Instalments
                            price={priceToInstalments}
                            topBorder={true}
                        />
                    </div>
                </div>
            </div>

            {buyNowModalVisible && (
                <Portal>
                    <ProductBoxBuyNowModal
                        options={finalPricingMatrixOptions}
                        product={product}
                        selectedOption={currentPricingMatrixOption}
                        onClose={() => setBuyNowModalVisible(false)}
                    />
                </Portal>
            )}
        </StyledComponent>
    );
};

export default PublicLayoutOfferProductCard;
